'use client';

import { type Condition } from '@repo/api-gw-sdk';
import { createContext, useContext, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { withBackupStatusExcluded, withoutBackupStatus } from '@/components/backupStatus/backupStatusQuery';
import { unsupportedStatusOf } from '@/components/backupStatus/configurationStatusScope';
import { toJsonText } from '@/components/queryBuilder/QueryLanguageConverter';
import { useViewParameters } from '@/contexts/navigation';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { PreferencesKey } from '@/data/dal/preferencesKey';

// Page-scoped context: what the inventory's settings panel holds, for the
// surfaces that answer to it — the panel itself, the filter bar and the download
// button, all of which render inside the inventory page.
interface InventorySettingsContextValue {
  // Whether the Unsupported backup status is offered in the inventory's filters.
  includeUnsupportedResources: boolean;
  setIncludeUnsupportedResources: (include: boolean) => void;
  // Whether the stored answer to the above has arrived. Only the correction that strips the status
  // out of an incoming link reads it: that rewrite replaces history, so running it while the setting
  // still reads off by default would drop a link's own filter for good.
  isSettingLoaded: boolean;
  // Which side holds the exclusion that keeps unsupported resources out of view:
  // the filters (it is in the URL, where the user can lift it) or the view (the
  // list query adds it).
  filtersOwnUnsupportedExclusion: boolean;
}
const InventorySettingsContext = createContext<InventorySettingsContextValue | null>(null);

/** Compares queries by value, the way `setParams` spots a query change of its own. */
const queryKey = (query: Condition | undefined) => query ? toJsonText(query) : '';
export const InventorySettingsProvider = ({
  children
}: PropsWithChildren) => {
  const {
    query,
    setParams
  } = useViewParameters();
  const dal = useDAL();
  const {
    InventoryStatusColumns
  } = useFeatureFlags();
  const unsupportedStatus = unsupportedStatusOf(InventoryStatusColumns);
  const {
    data: preferences,
    isLoading
  } = dal.preferences.useUserPref(PreferencesKey.Preferences);
  // The setting is persisted, but what the user picks this session leads it: the
  // handover below reads this value in the same render the picking writes the
  // query, which a value that only arrives once the write round-trips would miss.
  // Until the stored answer lands the setting reads off, which is the inventory's
  // own default — unsupported resources out of view.
  const [picked, setPicked] = useState<boolean>();
  const includeUnsupportedResources = picked ?? preferences?.value?.inventory?.includeUnsupportedResources ?? false;
  const handedOverToFiltersRef = useRef(false);
  const writtenQueryRef = useRef<Condition>(undefined);

  // Switching the setting writes the query the filters should hold, and the state
  // above beats that write to the reader by a render: `useSearchParams` only
  // updates once the router has processed the history entry. So the handover waits
  // for the write to arrive — otherwise that one render has neither side excluding
  // the status and the list fetches rows the user never asked to see.
  //
  // Waiting on the write rather than on the status appearing in the query is what
  // makes it arrive at all: turning the setting on while a positive status filter
  // is up writes no exclusion, because "is one of PROTECTED" already leaves
  // unsupported out, and a handover that waited to see the status would never
  // happen. Once handed over it stays that way — the user lifting the exclusion
  // takes the status back out of the query, and that must not pull it back here.
  //
  // Resolved as the render goes rather than in an effect: switching the setting off
  // has to take the exclusion back in the same render that stops trusting the
  // filters, or that render is the mirror of the gap above.
  if (!includeUnsupportedResources) {
    handedOverToFiltersRef.current = false;
  } else if (writtenQueryRef.current === undefined || queryKey(query) === queryKey(writtenQueryRef.current)) {
    handedOverToFiltersRef.current = true;
  }
  const setIncludeUnsupportedResources = (include: boolean) => {
    const next = include ? withBackupStatusExcluded(query, unsupportedStatus) : withoutBackupStatus(query, unsupportedStatus);

    // Held so the handover above can tell this write landing from the user's own
    // later edits, which is the difference between the filters owning the exclusion
    // and the user having lifted it.
    writtenQueryRef.current = include ? next : undefined;
    setPicked(include);

    // Re-read the shared ui-preferences bag right before writing so values another
    // component persisted meanwhile (theme, table columns) are not overwritten.
    void dal.preferences.getUserPrefAsync(PreferencesKey.Preferences).then(pref => dal.preferences.updateUserPref(PreferencesKey.Preferences, {
      ...pref?.value,
      inventory: {
        ...pref?.value?.inventory,
        includeUnsupportedResources: include
      }
    }));
    setParams(state => ({
      ...state,
      // The row count can change either way — switching off after the user lifted
      // the exclusion takes rows away — and the page the user is on may not
      // survive that. Reset here rather than leaning on the query change to do
      // it: switching off often rewrites nothing.
      pageIndex: 0,
      query: next
    }),
    // The setting does not live in the address, so this write is a consequence
    // of it rather than a step of its own. Pushed, one press of back would undo
    // the exclusion while leaving the setting on, and the resources it hides
    // would appear.
    {
      replace: true
    });
  };
  const value = useMemo(() => ({
    includeUnsupportedResources,
    setIncludeUnsupportedResources,
    isSettingLoaded: !isLoading,
    filtersOwnUnsupportedExclusion: handedOverToFiltersRef.current
  }),
  // `query` is a dependency twice over: the setter writes the query it reads, and
  // every path that moves the refs below also lands on it, so the value is
  // rebuilt in the same render the handover flips.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [includeUnsupportedResources, isLoading, query, setParams]);
  return <InventorySettingsContext.Provider value={value}>
      {children}
    </InventorySettingsContext.Provider>;
};
export const useInventorySettings = () => {
  const context = useContext(InventorySettingsContext);
  if (!context) {
    throw new Error("useInventorySettings must be used within an InventorySettingsProvider");
  }
  return context;
};

/**
 * Whether the Unsupported backup status may be offered as a filter value. For
 * shared filter code, which also renders on pages that have no inventory
 * settings panel to opt in with — there the status is offered as it always was.
 */
export const useIsUnsupportedStatusOffered = () => {
  return useContext(InventorySettingsContext)?.includeUnsupportedResources ?? true;
};